import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './styles.module.css';

interface IAiFollowUpsProps {
  /** At most `MAX_FOLLOW_UPS`, already read leniently (`followUpsOf`). */
  questions: readonly string[];
  /** The reader picked one. The surface sends it, exactly as if it were typed. */
  onPick: (question: string) => void;
  /**
   * `wrap` (the page) lets the chips take as many lines as they need; `row`
   * (the dashboard card) keeps them on one line that scrolls sideways, so three
   * long questions never take the card's transcript from it.
   */
  layout?: 'wrap' | 'row';
}

/**
 * The next questions the assistant suggests after its latest answer, as chips
 * docked above the composer (answer-surface spec, §2) — where the reader's eye
 * and hand already are for the next question, rather than under an answer that
 * scrolls away. A tap beats typing a sentence in Japanese on a tablet between
 * walks.
 *
 * The SURFACE decides when they show: after a whole answer, never while a turn
 * runs, and never while the reader is typing — so a click can only ever send
 * the chip, not race the reader's own half-written question.
 *
 * A CLICK SENDS. These are written by a model that has just read the site's
 * rows — `issue.description` among them, which anybody who can file an issue
 * writes — so their text can be steered. That is safe only because every tool
 * the assistant has READS: a steered question asks for what the reader may
 * already see, and the answer goes to them alone. The morning brief's questions
 * still draft (`useBriefHandover`); these are the deliberate exception, and a
 * tool that writes re-opens it (answer-surface spec, §2 "Why sent"). Plain text
 * for the same reason: never markdown, never a link, and read before pressed.
 */
export function AiFollowUps({ questions, onPick, layout = 'wrap' }: IAiFollowUpsProps) {
  const { t } = useTranslation();
  if (questions.length === 0) return null;
  const chips = questions.map((question) => (
    <button key={question} type="button" className={styles.chip} onClick={() => onPick(question)}>
      {question}
    </button>
  ));
  const label = t('aiAgent.followUps.label');
  if (layout === 'row') {
    return (
      <ScrollArea
        orientation="horizontal"
        className={styles.rowFrame}
        viewportClassName={`${styles.followUps} ${styles.row}`}
        viewportProps={{ role: 'group', 'aria-label': label }}
      >
        {chips}
      </ScrollArea>
    );
  }
  return (
    <div className={styles.followUps} role="group" aria-label={label}>
      {chips}
    </div>
  );
}
